import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Avatar, Snackbar, Alert, Chip, Divider,
} from "@mui/material";
import { SideBarWidth } from "../../utils/SideBarWidth";
import { hub } from "../../api/requests/hubs/hubs";
import { Colors } from "../../Theme/Theme";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

export default function AdminCommission() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [amount, setAmount] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchUsers = async () => {
    try {
      const response = await hub.getUsersWallet();
      setUsers(response?.users || []);
    } catch (error) {}
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleAddCommission = async () => {
    if (!selectedUser || !amount) {
      setSnackbar({ open: true, message: "Select a member and enter an amount", severity: "error" });
      return;
    }
    try {
      await hub.patchUpdateWalletDetails({
        userId: selectedUser.user?.id,
        amount: parseInt(amount),
      });
      setSnackbar({
        open: true,
        message: `Rs. ${parseInt(amount).toLocaleString("en-IN")} commission added to ${selectedUser.user?.name} successfully!`,
        severity: "success",
      });
      setAmount("");
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to add commission", severity: "error" });
    }
  };

  const calculateBalance = (walletHistory) => {
    if (!walletHistory) return 0;
    let credit = 0, withdrawal = 0;
    walletHistory.forEach((t) => {
      if (t.type === "CREDIT") credit += parseFloat(t.amount);
      else if (t.type === "WITHDRAWAL") withdrawal += parseFloat(t.amount);
    });
    return credit - withdrawal;
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px", fontSize: "14px",
      "& fieldset": { borderColor: Colors.dividerColor },
      "&:hover fieldset": { borderColor: Colors.primary },
      "&.Mui-focused fieldset": { borderColor: Colors.primary, borderWidth: "2px" },
    },
    "& .MuiInputLabel-root": { fontSize: "13px", color: Colors.secondaryColor },
    "& .MuiInputLabel-root.Mui-focused": { color: Colors.primary },
  };

  return (
    <Box sx={{
      width: { xs: "100%", md: `calc(100% - ${SideBarWidth}px)` },
      ml: { md: `${SideBarWidth}px` },
      minHeight: "100vh", backgroundColor: Colors.bgColor,
    }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3, maxWidth: 1200 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <MonetizationOnIcon sx={{ fontSize: 22, color: "#F59E0B" }} />
            <Typography sx={{ fontWeight: 700, color: Colors.primaryTextColor, fontSize: { xs: "20px", md: "24px" }, letterSpacing: "-0.02em" }}>
              Commission Management
            </Typography>
          </Box>
          <Typography sx={{ color: Colors.secondaryColor, fontSize: "13px" }}>
            Add commission/credits to member wallets manually.
          </Typography>
        </Box>

        {/* Add Commission Form */}
        {selectedUser && (
          <Box sx={{
            backgroundColor: Colors.white, borderRadius: "12px", mb: 3,
            border: `1px solid ${Colors.dividerColor}`, overflow: "hidden",
          }}>
            <Box sx={{ px: 3, py: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "15px", color: Colors.primaryTextColor }}>
                Add Commission
              </Typography>
            </Box>
            <Divider sx={{ borderColor: Colors.dividerColor }} />
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5, p: 2, borderRadius: "10px", backgroundColor: Colors.blueSoft }}>
                <Avatar sx={{ width: 40, height: 40, backgroundColor: Colors.primary, color: "#FFF", fontSize: "14px", fontWeight: 700 }}>
                  {selectedUser.user?.name?.charAt(0)?.toUpperCase() || "U"}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: "14px", color: Colors.primaryTextColor }}>{selectedUser.user?.name}</Typography>
                  <Typography sx={{ fontSize: "12px", color: Colors.secondaryColor }}>{selectedUser.user?.email} | Level {selectedUser.level}</Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography sx={{ fontSize: "10px", fontWeight: 600, color: Colors.secondaryColor, textTransform: "uppercase" }}>Current Balance</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: "16px", color: Colors.primary }}>
                    Rs. {calculateBalance(selectedUser.walletHistory).toLocaleString("en-IN")}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
                <TextField
                  label="Commission Amount (Rs.)" type="number" size="small"
                  value={amount} onChange={(e) => setAmount(e.target.value)}
                  sx={{ ...fieldSx, flex: 1, maxWidth: 300 }}
                />
                <Button
                  variant="contained" onClick={handleAddCommission}
                  startIcon={<ArrowUpwardIcon sx={{ fontSize: 16 }} />}
                  disabled={!amount}
                  sx={{
                    backgroundColor: Colors.insuranceGreen, fontWeight: 600, fontSize: "13px",
                    borderRadius: "10px", px: 3, textTransform: "none", boxShadow: "none",
                    "&:hover": { backgroundColor: "#047857" },
                    "&.Mui-disabled": { backgroundColor: "#E2E8F0", color: "#94A3B8" },
                  }}
                >
                  Add Commission
                </Button>
                <Button
                  variant="outlined" onClick={() => { setSelectedUser(null); setAmount(""); }}
                  sx={{
                    borderColor: Colors.dividerColor, color: Colors.secondaryColor, fontWeight: 600,
                    fontSize: "13px", borderRadius: "10px", textTransform: "none",
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Members List */}
        <Box sx={{ backgroundColor: Colors.white, borderRadius: "12px", border: `1px solid ${Colors.dividerColor}`, overflow: "hidden" }}>
          <Box sx={{ px: 3, py: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "15px", color: Colors.primaryTextColor }}>
              Members ({users.length})
            </Typography>
          </Box>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  {["Member", "Shortcode", "Level", "Wallet Balance", "Actions"].map((h) => (
                    <TableCell key={h} sx={{
                      color: Colors.secondaryColor, py: 1.5, px: 2.5,
                      fontWeight: 600, fontSize: "11px", letterSpacing: "0.5px",
                      textTransform: "uppercase", backgroundColor: Colors.bgColor,
                      borderBottom: `1px solid ${Colors.dividerColor}`,
                    }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((item) => {
                  const u = item?.user;
                  const balance = calculateBalance(item?.walletHistory);
                  return (
                    <TableRow key={u?.id} sx={{
                      "&:hover": { backgroundColor: Colors.bgColor },
                      backgroundColor: selectedUser?.user?.id === u?.id ? Colors.blueSoft : "transparent",
                    }}>
                      <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, py: 1.5, px: 2.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, backgroundColor: Colors.blueSoft, color: Colors.primary, fontSize: "12px", fontWeight: 700 }}>
                            {u?.name?.charAt(0)?.toUpperCase() || "U"}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: "13px", color: Colors.primaryTextColor }}>{u?.name || "---"}</Typography>
                            <Typography sx={{ fontSize: "11px", color: Colors.secondaryColor }}>{u?.email || "---"}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, py: 1.5, px: 2.5 }}>
                        <Typography sx={{ fontSize: "13px", fontFamily: "monospace", color: Colors.primaryTextColor }}>{u?.shortcode || "---"}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, py: 1.5, px: 2.5 }}>
                        <Chip label={`Level ${item?.level || "---"}`} size="small" sx={{
                          height: 22, fontSize: "11px", fontWeight: 600,
                          backgroundColor: Colors.blueSoft, color: Colors.primary,
                        }} />
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, py: 1.5, px: 2.5 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: "14px", color: balance >= 0 ? Colors.insuranceGreen : "#EF4444" }}>
                          Rs. {balance.toLocaleString("en-IN")}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${Colors.dividerColor}`, py: 1.5, px: 2.5 }}>
                        <Button
                          size="small" variant="outlined"
                          onClick={() => { setSelectedUser(item); setAmount(""); }}
                          startIcon={<MonetizationOnIcon sx={{ fontSize: 14 }} />}
                          sx={{
                            borderColor: "#FDE68A", color: "#F59E0B", fontSize: "11px",
                            fontWeight: 600, borderRadius: "6px", textTransform: "none", px: 1.5,
                            "&:hover": { backgroundColor: "#FFFBEB", borderColor: "#F59E0B" },
                          }}
                        >
                          Add Commission
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ borderRadius: "10px" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
